'use client';

import { useRef, useId, useState, useEffect, ReactNode } from 'react';
import styles from './video.module.scss';
import cn from 'classnames';
import { Text } from '@/ui-components/atoms/Text/Text';
import { IconVideoPlayButton } from '@/assets/icons/IconVideoPlayButton';
import { IconVideoPauseButton } from '@/assets/icons/IconVideoPauseButton';
import { IconCaption } from '@/assets/icons/IconCaption';
import { IconVolume } from '@/assets/icons/IconVolume';
import { IconDesktopView } from '@/assets/icons/IconDesktopView';
import { IconMutedVolume } from '@/assets/icons/IconMutedVolume';

export interface VideoModalProps {
    poster?: string;
    source?: string;
    title?: string;
    description?: ReactNode;
    videoId?: string;
    isModalOpen?: boolean;
    className?: string;
    activeVideo?: boolean;
}

export function VideoModal(props: VideoModalProps): React.JSX.Element {
    const videoRef = useRef<HTMLVideoElement>(null);
    const youtubePlayerRef = useRef<HTMLIFrameElement | null>(null);
    const generatedId = useId();
    const [isPlayingInternal, setIsPlayingInternal] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.2);
    const [isHovered, setIsHovered] = useState(false);
    const [isYoutubeIframeLoaded, setIsYoutubeIframeLoaded] = useState(false);
    const videoModalClassNames = cn(styles.modalVideoContainer, props.className);

    const isPlaying = props.isModalOpen ? isPlayingInternal : false;
    const youtubeEmbedUrl = props.videoId ? `https://www.youtube.com/embed/${props.videoId}` : null;
    const youtubeOrigin = typeof window === 'undefined' ? '' : window.location.origin;
    const youtubeQueryParams = new URLSearchParams({
        enablejsapi: '1',
        rel: '0',
        modestbranding: '1',
        controls: '1',
        playsinline: '1',
        ...(youtubeOrigin ? { origin: youtubeOrigin } : {}),
    });
    const youtubeFullUrl = `${youtubeEmbedUrl}?${youtubeQueryParams}`;
    const youtubeElementId = youtubeEmbedUrl?.split('/').pop()?.split('?')[0] ?? '';

    useEffect(() => {
        const videoElement = videoRef.current;
        if (videoElement) {
            const updateTime = () => setCurrentTime(videoElement.currentTime);
            const updateDuration = () => setDuration(videoElement.duration);

            videoElement.addEventListener('timeupdate', updateTime);
            videoElement.addEventListener('loadedmetadata', updateDuration);

            return () => {
                videoElement.removeEventListener('timeupdate', updateTime);
                videoElement.removeEventListener('loadedmetadata', updateDuration);
            };
        }
    }, []);

    useEffect(() => {
        const videoElement = videoRef.current;
        if (!props.isModalOpen || !props.activeVideo || props.videoId || !videoElement) return;

        const handleCanPlay = () => {
            videoElement.play();
        };
        videoElement.addEventListener('canplay', handleCanPlay);
        videoElement.load();

        return () => {
            videoElement.removeEventListener('canplay', handleCanPlay);
            videoElement.pause();
            setIsPlayingInternal(false);
        };
    }, [props.isModalOpen, props.activeVideo, props.videoId]);

    useEffect(() => {
        if (props.isModalOpen) return;

        const videoElement = videoRef.current;
        if (videoElement) {
            videoElement.pause();
            videoElement.currentTime = 0;
        } else if (youtubePlayerRef.current && isYoutubeIframeLoaded) {
            youtubePlayerRef.current.contentWindow?.postMessage(
                JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
                'https://www.youtube.com',
            );
        }
    }, [props.isModalOpen, isYoutubeIframeLoaded]);

    const togglePlayPause = () => {
        const videoElement = videoRef.current;
        if (videoElement) {
            if (isPlaying) {
                videoElement.pause();
            } else {
                videoElement.play();
            }
        }
    };

    const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const videoElement = videoRef.current;
        const volumeValue = Number(event.target.value);
        if (videoElement) {
            videoElement.volume = volumeValue;
        }
        setVolume(volumeValue);
    };

    const handleTimingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const videoElement = videoRef.current;
        const seekTime = Number(event.target.value);
        if (videoElement) {
            videoElement.currentTime = seekTime;
            setCurrentTime(seekTime);
        }
    };

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const handleFullScreen = () => {
        if (videoRef.current) {
            if (videoRef.current.requestFullscreen) {
                videoRef.current.requestFullscreen();
            }
        }
    };

    const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <>
            {!props?.videoId && props.source && (
                <div
                    className={videoModalClassNames}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <div className={styles.videoContent}>
                        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- Captions require a CMS-provided VTT file and a captionSrc prop (see VideoPlayer.tsx for reference implementation). */}
                        <video
                            ref={videoRef}
                            playsInline
                            poster={props.poster}
                            aria-describedby={generatedId}
                            preload="none"
                            onPlay={() => setIsPlayingInternal(true)}
                            onPause={() => setIsPlayingInternal(false)}
                            onEnded={() => setIsPlayingInternal(false)}
                        >
                            <source src={props.source} type="video/mp4" />
                        </video>
                    </div>
                    {isHovered && (
                        <div className={styles.videoControlsContainer}>
                            <div className={styles.videoTiming}>
                                <div className={styles.badgeTiming}>
                                    <div className={styles.currentTime}>
                                        <span className={styles.timeDisplay}>{formatTime(currentTime)}</span>
                                    </div>
                                    <div className={styles.remainingTime}>
                                        <span className={styles.timeDisplay}>
                                            -{formatTime(duration - currentTime)}
                                        </span>
                                    </div>
                                </div>
                                <input
                                    type="range"
                                    className={styles.timeProgressBar}
                                    min="0"
                                    max={duration}
                                    step="0.1"
                                    value={currentTime}
                                    onChange={handleTimingChange}
                                    style={{
                                        background: `linear-gradient(to right, #ffffff ${progressPercentage}%, rgba(255, 255, 255, 0.20) ${progressPercentage}%)`,
                                    }}
                                />
                            </div>
                            <div className={styles.controllerContainer}>
                                <div className={styles.videoTitleContainer}>
                                    <Text tag="p" className={styles.videoTitle}>
                                        {props.description}
                                    </Text>
                                </div>
                                <div className={styles.playPauseContainer}>
                                    <button className={styles.playPauseButton} onClick={togglePlayPause}>
                                        {isPlaying ? <IconVideoPauseButton /> : <IconVideoPlayButton />}
                                    </button>
                                </div>
                                <div className={styles.rightContainer}>
                                    <div className={styles.ccButtonContainer}>
                                        <button className={styles.captionButton}>
                                            <IconCaption />
                                        </button>
                                    </div>
                                    <div className={styles.innerRightContainer}>
                                        <div className={styles.volumeContainer}>
                                            <button className={styles.volumeButton}>
                                                {volume > 0 && <IconVolume />}
                                                {volume === 0 && <IconMutedVolume />}
                                            </button>
                                            <input
                                                type="range"
                                                className={styles.volumeControl}
                                                min="0"
                                                max="1"
                                                step="0.1"
                                                value={volume}
                                                onChange={handleVolumeChange}
                                                style={{
                                                    background: `linear-gradient(to right, #ffffff ${volume * 100}%, rgba(255, 255, 255, 0.20) ${volume * 100}%)`,
                                                }}
                                            />
                                        </div>

                                        <button className={styles.desktopViewButton} onClick={handleFullScreen}>
                                            <IconDesktopView />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {props?.videoId && youtubeFullUrl && (
                <div className={props.className} data-sxmyoutubeplayer>
                    <iframe
                        src={youtubeFullUrl}
                        ref={youtubePlayerRef}
                        onLoad={() => setIsYoutubeIframeLoaded(true)}
                        title={props.title}
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                        className={styles.youtubeVideo}
                        id={youtubeElementId}
                    />
                </div>
            )}
        </>
    );
}
